import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { CloseIcon, MenuIcon, QrCodeIcon } from "../../shared/components/icons";
import { useT } from "../../shared/i18n";
import { APP_BAR_HEIGHT } from "./layout";
import type { Shell } from "./useShell";
import { useDemoMode } from "../demo/useDemoMode";

/**
 * The one app bar: burger + search + QR on phone, centred search + QR on
 * tablet/desktop. It carries no title, never swaps and never hides (spec §2–3);
 * only its leading control changes with the shell.
 */
export function TopBar({
  shell,
  searchValue,
  onSearchChange,
  onSearchFocus,
  onMenu,
  onScan,
}: {
  shell: Shell;
  /** Mirrors `q` on /search and is empty everywhere else (spec §5). */
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchFocus: () => void;
  onMenu: () => void;
  onScan: () => void;
}) {
  const t = useT();
  const demoMode = useDemoMode();
  const phone = shell === "phone";

  return (
    <Box
      component="header"
      data-testid="app-bar"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        minHeight: APP_BAR_HEIGHT,
        flexShrink: 0,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        pt: "env(safe-area-inset-top)",
        boxSizing: "border-box",
      }}
    >
      {phone ? (
        <IconButton aria-label={t("menu")} onClick={onMenu} data-testid="nav-menu">
          <MenuIcon fontSize="small" />
        </IconButton>
      ) : (
        <Box sx={{ flex: 1 }} />
      )}

      <Box
        sx={{
          flex: phone ? 1 : "0 1 560px",
          display: "flex",
          justifyContent: "center",
          minWidth: 0,
        }}
      >
        <TextField
          size="small"
          variant="outlined"
          placeholder={t("searchPlaceholder")}
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          onFocus={onSearchFocus}
          autoComplete="off"
          sx={{
            width: "100%",
            maxWidth: 560,
            // Outlined and square: the shell base radius, never a filled field
            // (spec §8).
            "& .MuiOutlinedInput-root": { borderRadius: 1 },
          }}
          slotProps={{
            htmlInput: {
              // The accessible name is its own key; the placeholder is only a
              // hint (shell-redesign 12, spec §9–10).
              "aria-label": t("search"),
              "data-search-field": "true",
              type: "search",
            },
            input: {
              // The clear affordance is part of the search lifecycle: it drops
              // `q` with replace, so the results fall back to recents
              // (shell-redesign 09, spec §5). Native `type=search` decorations
              // are not reliable across browsers, so the button is explicit.
              endAdornment: searchValue ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t("clearSearch")}
                    onClick={() => onSearchChange("")}
                    data-testid="clear-search"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      </Box>

      <Box sx={{ flex: phone ? "0 0 auto" : 1, display: "flex", justifyContent: "flex-end" }}>
        {/* Scanning adds items/containers, so the demo has no scan entry point. */}
        {!demoMode && (
          <IconButton aria-label={t("scanTitle")} onClick={onScan} data-testid="qr-button">
            <QrCodeIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
